<style scoped>
  .ai-layout {
    display: flex;
    height: calc(100vh - 75px);
    background: #fff;
    min-height: 400px;
  }

  .ai-sidebar {
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e7eaec;
  }

  .ai-sidebar-header {
    padding: 12px;
    border-bottom: 1px solid #e7eaec;
  }

  .ai-sidebar-list {
    flex: 1;
    overflow-y: auto;
  }

  .ai-page-conversation {
    display: block;
    padding: 12px 16px;
    border-bottom: 1px solid #e7eaec;
    cursor: pointer;
    color: inherit;
  }

  .ai-page-conversation:hover {
    background: #f3fbf9;
  }

  .ai-page-conversation.active {
    background: #f3fbf9;
    border-left: 3px solid #1ab394;
    padding-left: 13px;
  }

  .ai-page-conversation-title {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ai-page-conversation-meta {
    display: block;
    font-size: 11px;
    color: #999;
  }

  .ai-sidebar-section {
    margin: 0;
    padding: 8px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
    background: #f5f6f8;
    border-bottom: 1px solid #e7eaec;
  }

  .ai-page-conversation.unread .ai-page-conversation-title {
    font-weight: 700;
  }

  .unread-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1ab394;
    margin-right: 4px;
  }

  .ai-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ai-toolbar {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 16px;
    border-bottom: 1px solid #e7eaec;
  }

  .ai-context-link {
    color: #1ab394;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ai-context-link:hover {
    text-decoration: underline;
  }

  .ai-page-files {
    font-size: 12px;
    color: #676a6c;
    background: #f8f9fa;
    border-bottom: 1px solid #e7eaec;
    padding: 8px 16px;
  }

  .ai-page-file {
    display: inline-block;
    margin-left: 10px;
    white-space: nowrap;
  }

  .ai-page-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
  }

  .ai-page-message {
    margin-bottom: 12px;
    padding: 10px 14px;
    border-radius: 4px;
    max-width: 80%;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .ai-page-message.user {
    background: #1ab394;
    color: #fff;
    margin-left: auto;
  }

  .ai-page-message.user >>> a {
    color: #fff;
  }

  .ai-page-message.assistant {
    background: #f3f3f4;
    white-space: normal;
  }

  .ai-page-message.assistant >>> p,
  .ai-page-message.assistant >>> ul,
  .ai-page-message.assistant >>> ol,
  .ai-page-message.assistant >>> table,
  .ai-page-message.assistant >>> pre,
  .ai-page-message.assistant >>> h1,
  .ai-page-message.assistant >>> h2,
  .ai-page-message.assistant >>> h3,
  .ai-page-message.assistant >>> h4 {
    margin: 0 0 8px;
  }

  .ai-page-message.assistant >>> *:last-child {
    margin-bottom: 0;
  }

  .ai-page-message.assistant >>> h1 { font-size: 18px; }
  .ai-page-message.assistant >>> h2 { font-size: 16px; }
  .ai-page-message.assistant >>> h3,
  .ai-page-message.assistant >>> h4 { font-size: 14px; }

  .ai-page-message.assistant >>> ul,
  .ai-page-message.assistant >>> ol {
    padding-left: 20px;
  }

  .ai-page-message.assistant >>> table {
    border-collapse: collapse;
  }

  .ai-page-message.assistant >>> th,
  .ai-page-message.assistant >>> td {
    border: 1px solid #ddd;
    padding: 4px 8px;
    text-align: left;
  }

  .ai-page-message.assistant >>> th {
    background: #eee;
  }

  .ai-page-message.assistant >>> code {
    background: #e8e8e8;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 0.9em;
  }

  .ai-page-message.assistant >>> pre {
    background: #e8e8e8;
    border-radius: 3px;
    padding: 8px;
    overflow-x: auto;
  }

  .ai-page-message.assistant >>> pre code {
    background: none;
    padding: 0;
  }

  .ai-composer-pending {
    font-size: 12px;
    color: #676a6c;
    padding: 6px 16px;
    border-top: 1px solid #e7eaec;
    background: #f8f9fa;
  }

  .ai-composer-pending a {
    color: #999;
    cursor: pointer;
    margin-left: 8px;
  }

  .ai-composer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid #e7eaec;
  }

  .ai-composer-attach {
    color: #676a6c;
    cursor: pointer;
  }

  .ai-composer input[type=text] {
    flex: 1;
  }
</style>

<template>
  <div>
    <app-header>
      <template #title>
        <h1 class="d-inline me-4">AI Chat</h1>
      </template>
    </app-header>

    <div class="ai-layout">
      <aside class="ai-sidebar">
        <div class="ai-sidebar-header">
          <button class="btn btn-primary w-100" @click="newChat">
            <i class="far fa-plus"></i> New Chat
          </button>
        </div>

        <div class="ai-sidebar-list">
          <p class="text-muted p-3 mb-0" v-if="!conversations.length">No conversations yet.</p>

          <template v-if="scheduledConversations.length">
            <p class="ai-sidebar-section">
              Scheduled
              <span v-if="unreadScheduledCount" class="badge rounded-pill bg-danger">{{ unreadScheduledCount }}</span>
            </p>

            <a
              v-for="conversation in scheduledConversations"
              :key="conversation.id"
              class="ai-page-conversation"
              :class="{ active: conversation.id === conversationId, unread: !conversation.read_at }"
              @click="openConversation(conversation)"
            >
              <span class="ai-page-conversation-title">
                <span v-if="!conversation.read_at" class="unread-dot"></span>
                {{ conversation.title }}
              </span>
              <span class="ai-page-conversation-meta">
                {{ agentLabels[conversation.agent] }} ·
                {{ conversation.updated_at | moment("DD/MM/YYYY HH:mm") }}
              </span>
            </a>

            <p class="ai-sidebar-section">Chats</p>
          </template>

          <a
            v-for="conversation in chatConversations"
            :key="conversation.id"
            class="ai-page-conversation"
            :class="{ active: conversation.id === conversationId }"
            @click="openConversation(conversation)"
          >
            <span class="ai-page-conversation-title">{{ conversation.title }}</span>
            <span class="ai-page-conversation-meta">
              {{ agentLabels[conversation.agent] }} ·
              {{ conversation.updated_at | moment("DD/MM/YYYY HH:mm") }}
            </span>
          </a>
        </div>
      </aside>

      <main class="ai-main">
        <div class="ai-toolbar">
          <select
            class="form-select w-auto"
            v-model="agent"
            :disabled="!!conversationId"
            :title="conversationId ? 'The agent is fixed for an existing conversation' : ''"
          >
            <option v-for="(label, value) in availableAgents" :key="value" :value="value">
              {{ label }}
            </option>
          </select>

          <router-link
            v-if="conversationContext && conversationContext.type === 'opportunity'"
            class="ai-context-link"
            :to="{ name: 'opportunities.overview', params: { opportunity_id: conversationContext.id } }"
            title="Open the opportunity this conversation is about"
          >
            <i class="fal fa-bullseye"></i>
            Opportunity #{{ conversationContext.id }} · {{ conversationContext.name }}
          </router-link>

          <router-link
            v-if="conversationContext && conversationContext.type === 'project'"
            class="ai-context-link"
            :to="{ name: 'projects.show.details', params: { project_id: conversationContext.id } }"
            title="Open the project this conversation is about"
          >
            <i class="fal fa-folder"></i>
            Project #{{ conversationContext.id }} · {{ conversationContext.name }}
          </router-link>
        </div>

        <div v-if="conversationFiles.length" class="ai-page-files">
          <i class="fal fa-paperclip"></i> Context files:
          <span v-for="file in conversationFiles" :key="file.id" class="ai-page-file">
            <i class="fal" :class="fileIcon(file.kind)"></i> {{ file.name }}
          </span>
        </div>

        <div class="ai-page-messages" ref="messages">
          <p class="text-muted" v-if="!messages.length">
            Start a new conversation, or pick one from the left.
          </p>

          <div
            v-for="(message, index) in messages"
            :key="index"
            class="ai-page-message"
            :class="message.role"
            v-html="renderMessage(message)"
          ></div>

          <div v-if="sending" class="ai-page-message assistant">
            <i class="fal fa-spinner fa-pulse"></i>
          </div>
        </div>

        <div v-if="pendingAttachment" class="ai-composer-pending">
          <i class="fal fa-paperclip"></i> {{ pendingAttachment.name }}
          <a @click="pendingAttachment = null" title="Remove attachment"><i class="fal fa-times"></i></a>
        </div>

        <div class="ai-composer">
          <a class="ai-composer-attach" @click="$refs.attachmentInput.click()" title="Attach a small file (PDF, image, text or CSV) - read directly into the conversation">
            <i class="fal fa-paperclip"></i>
          </a>
          <input
            type="file"
            ref="attachmentInput"
            style="display: none"
            accept=".pdf,.txt,.csv,image/*"
            @change="chooseAttachment"
          />
          <input
            type="text"
            class="form-control"
            v-model="draft"
            placeholder="Type a message..."
            :disabled="sending"
            @keyup.enter="send"
          />
          <button class="btn btn-primary" :disabled="sending || !draft.trim()" @click="send">
            <i class="fal fa-paper-plane"></i> Send
          </button>
        </div>
      </main>
    </div>
  </div>
</template>


<script>
import { http, notify } from 'utils'
import { AGENT_LABELS, availableAgents, attachmentError, fileIcon, renderMessage } from './chat'

export default {
  data: function () {
    return {
      conversations: [],
      conversationId: null,
      messages: [],
      conversationFiles: [],
      conversationContext: null,
      agent: 'assistant',
      draft: '',
      sending: false,
      pendingAttachment: null,
    }
  },

  computed: {
    agentLabels() {
      return AGENT_LABELS
    },

    availableAgents() {
      return availableAgents(this.$isA('admin'))
    },

    scheduledConversations() {
      return this.conversations.filter(c => c.scheduled_task_id)
    },

    chatConversations() {
      return this.conversations.filter(c => !c.scheduled_task_id)
    },

    unreadScheduledCount() {
      return this.scheduledConversations.filter(c => !c.read_at).length
    },
  },

  created: function () {
    this.loadConversations()
  },

  methods: {
    renderMessage,

    fileIcon,

    async loadConversations() {
      try {
        const { data } = await http.get('ai/conversations')
        this.conversations = data.data
      } catch (error) {
        this.conversations = []
      }
    },

    newChat() {
      this.conversationId = null
      this.messages = []
      this.conversationFiles = []
      this.conversationContext = null
    },

    async openConversation(conversation) {
      try {
        const { data } = await http.get(`ai/chat/${conversation.id}`)

        this.agent = conversation.agent
        this.conversationId = conversation.id
        this.messages = data.data.map((message) => ({
          role: message.role,
          content: message.content,
        }))
        this.conversationFiles = data.files || []
        this.conversationContext = data.context || null

        // Opening a scheduled run marks it read server-side - mirror that.
        if (conversation.scheduled_task_id && !conversation.read_at) {
          conversation.read_at = new Date().toISOString()
        }

        this.scrollToBottom()
      } catch (error) {
        notify.error('Failed to load conversation, please try again.')
      }
    },

    chooseAttachment(event) {
      const file = event.target.files[0]
      event.target.value = null

      if (!file) return

      const error = attachmentError(file)

      if (error) {
        notify.error(error)
        return
      }

      this.pendingAttachment = file
    },

    async send() {
      if (!this.draft.trim() || this.sending) return

      const message = this.draft.trim()
      const startedNew = !this.conversationId
      const attachment = this.pendingAttachment

      this.messages.push({
        role: 'user',
        content: message + (attachment ? '\n📎 ' + attachment.name : ''),
      })
      this.draft = ''
      this.pendingAttachment = null
      this.sending = true
      this.scrollToBottom()

      try {
        const form = new FormData()
        form.append('message', message)
        form.append('agent', this.agent)
        if (this.conversationId) form.append('conversation_id', this.conversationId)
        if (attachment) form.append('attachment', attachment)

        const { data } = await http.post('ai/chat', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        this.conversationId = data.data.conversation_id
        this.messages.push({ role: data.data.role, content: data.data.content })

        // A new conversation now exists (with a generated title) - refresh the list.
        if (startedNew) {
          this.loadConversations()
        }
      } catch (error) {
        notify.error('Failed to send message, please try again.')
      } finally {
        this.sending = false
        this.scrollToBottom()
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messages) {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
        }
      })
    },
  },
}
</script>
